import { Component, computed, DestroyRef, inject, type OnInit, signal, type WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map } from 'rxjs';

import type { DiagnosticDto, InstagramPageInfoDto } from '@malou-io/package-dto';

import { HeapEventName, type MaloupeEventName, type MaloupeLocale, PlatformKey } from '@malou-io/package-utils';

import { AuthContext } from ':core/context/auth.context';
import { CategoryHttpService } from ':core/http-services/category.http-service';
import { DiagnosticHttpService } from ':core/http-services/diagnostic.http-service';
import { ToastService } from ':core/services/toast.service';
import { ToastDuration } from ':shared/components/toast/toast-item/toast-item.component';
import { LIMITS_AND_TIME_BY_LOCAL_KEY } from ':shared/constants';
import { RequestLocalStorageKey } from ':shared/enums';
import { SvgIcon } from ':shared/enums/svg-icon.enum';
import { type Category, Diagnostic } from ':shared/models';
import { buildNewRequestDetails } from ':shared/utils';
import { getUserIdentityFromLocalStorage } from ':shared/utils/manage-user-uuid';

const DEFAULT_CATEGORY_ID = 'gcid:restaurant';

@Component({
    template: '',
    standalone: true,
    providers: [{ provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, useValue: { overlayPanelClass: 'custom-option-panel' } }],
})
export abstract class RestaurantDiagnosticSearchAbstractComponent implements OnInit {
    private readonly _categoryHttpService = inject(CategoryHttpService);
    private readonly _diagnosticHttpService = inject(DiagnosticHttpService);
    private readonly _authContext = inject(AuthContext);
    private readonly _destroyRef = inject(DestroyRef);
    protected readonly _translateService = inject(TranslateService);
    protected readonly _router = inject(Router);
    protected readonly _formBuilder = inject(FormBuilder);
    protected readonly _toastService = inject(ToastService);
    protected readonly _activatedRoute = inject(ActivatedRoute);

    readonly SvgIcon = SvgIcon;
    readonly PlatformKey = PlatformKey;

    readonly categoryControl = this._formBuilder.control({ value: '', disabled: true });
    readonly instagramControl = this._formBuilder.control<string | null>({ value: null, disabled: true });
    readonly autocompleteErrorMessage = signal<string | undefined>(undefined);

    readonly locale: MaloupeLocale = this._translateService.currentLang as MaloupeLocale;
    readonly instagramPage: WritableSignal<InstagramPageInfoDto | null> = signal(null);
    readonly instagramPageValidated: WritableSignal<boolean> = signal(false);

    readonly categories = toSignal(
        this._categoryHttpService.getAllCategories$().pipe(
            map((res) =>
                res.data.sort((a, b) => a.getCategoryNameForLang(this.locale).localeCompare(b.getCategoryNameForLang(this.locale)))
            ),
            takeUntilDestroyed(this._destroyRef)
        ),
        { initialValue: [] }
    );
    readonly categoryValue = toSignal(this.categoryControl.valueChanges.pipe(map((c) => (c === null ? '' : c))), { initialValue: '' });
    readonly hasCategory = computed(() => this.categoryValue() !== '');
    readonly filteredCategories = computed(() =>
        this.categoryValue() ? this._filterCategories(this.categoryValue()) : this.categories().slice()
    );
    readonly defaultCategory = computed(() => this.categories().find((c) => c.categoryId === DEFAULT_CATEGORY_ID) ?? this.categories()[0]);
    readonly diagnostic = signal<Diagnostic | null>(null);

    get category(): string | null {
        return this.categoryControl.value;
    }

    /**
     * Rate limit the number of requests
     */
    readonly getPlaceDiagnosticRateLimitPerDayExceeded: WritableSignal<boolean> = signal(false);

    /**
     * Query params for hubspot form
     */
    protected _utmSource = '';
    protected _utmMedium = '';
    protected _utmCampaign = '';

    /**
     * Event name
     */
    protected _eventName: MaloupeEventName | null = null;

    ngOnInit(): void {
        this._diagnosticHttpService
            .trackDiagnosticAction$({
                identity: getUserIdentityFromLocalStorage() ?? '',
                eventName: HeapEventName.MALOUPE_TRACKING_OPENING_LANDING_PAGE,
            })
            .subscribe();
        this.getPlaceDiagnosticRateLimitPerDayExceeded.set(this._isGetPlaceDiagnosticLimitExceeded());
        this._activatedRoute.queryParamMap.subscribe((params) => {
            this._utmSource = params.get('utm_source') ?? '';
            this._utmMedium = params.get('utm_medium') ?? '';
            this._utmCampaign = params.get('utm_campaign') ?? '';
            this._eventName = params.get('event_name') as MaloupeEventName;
        });
    }

    onLocationSelected(event: google.maps.places.PlaceResult): void {
        if (!event || !event.place_id || this.getPlaceDiagnosticRateLimitPerDayExceeded()) {
            return;
        }
        const placeId = event.place_id;
        this._diagnosticHttpService
            .createDiagnostic$(placeId, this.locale)
            .pipe(
                catchError((_error) => {
                    this._toastService.openErrorToast('Une erreur est survenue, veuillez réessayer plus tard.', ToastDuration.MEDIUM);
                    return [];
                })
            )
            .subscribe((diagnostic) => {
                this.diagnostic.set(new Diagnostic(diagnostic));
                const category = this._getCategoryFromDiagnostic(diagnostic);
                this.categoryControl.setValue(category.getCategoryNameForLang(this.locale));
                this.autocompleteErrorMessage.set(undefined);
                this.categoryControl.enable();
                this.instagramControl.enable();
            });
    }

    onInputChanged(event: string): void {
        if (event === '') {
            this.categoryControl.disable();
            this.categoryControl.setValue('');
            this.instagramControl.disable();
            this.instagramControl.setValue(null);
            return;
        }
    }

    onLocationCategoryChanged(category: Category): void {
        const diagnostic = this.diagnostic();
        if (diagnostic) {
            this._diagnosticHttpService.updateRestaurantCategory$(diagnostic.id, category.categoryId).subscribe((diagnosticResult) => {
                this.diagnostic.set(new Diagnostic(diagnosticResult));
            });
        }
    }

    abstract computeMyDiagnostic(): void;

    onSelectedInstagramPage(): void {
        this.instagramPageValidated.set(true);
    }

    onInstagramPageChanged(instagramPage: InstagramPageInfoDto | null): void {
        if (instagramPage === null) {
            this.instagramPageValidated.set(false);
            this.instagramPage.set(null);
            return;
        }
        this.instagramPage.set(instagramPage);
    }

    private _filterCategories(value: string): Category[] {
        const filterValue = value.toLowerCase();
        return this.categories().filter((c) => c.getCategoryNameForLang(this.locale).toLowerCase().includes(filterValue));
    }

    private _getCategoryFromDiagnostic(diagnostic: DiagnosticDto): Category {
        const restaurantCategory = diagnostic.restaurant.category;
        const findCategory = this.categories().find((c) => c.categoryId === restaurantCategory.categoryId);
        return findCategory ?? this.defaultCategory();
    }

    protected _isGetPlaceDiagnosticLimitExceeded(): boolean {
        const key = RequestLocalStorageKey.GET_PLACE_DIAGNOSTIC_REQUEST;
        const newPlaceDiagnosticRequestDetails = buildNewRequestDetails({
            key,
            shouldIncreaseCount: false,
        });

        localStorage.setItem(key, JSON.stringify(newPlaceDiagnosticRequestDetails));

        if (newPlaceDiagnosticRequestDetails.requestCount < LIMITS_AND_TIME_BY_LOCAL_KEY[key].limit || this._authContext.isTokenValid()) {
            this.getPlaceDiagnosticRateLimitPerDayExceeded.set(false);
            return false;
        }
        this.getPlaceDiagnosticRateLimitPerDayExceeded.set(true);
        return true;
    }
}
